import React from 'react';
import { Datepicker, FlowbiteDatepickerTheme } from 'flowbite-react';
import { DeepPartial } from 'flowbite-react/dist/types/types';
import SVGImage from '../layout/SVGImage';
import { useTranslation } from 'react-i18next';

interface DateSelectorProps {
  date: Date;
  className?: string;
  inputBase?: string;
  colors?: string;
  testId?: string;
  disabled?: boolean;
  onChange?: (date: Date | null) => void;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  date,
  className,
  inputBase,
  disabled,
  colors,
  testId,
  onChange,
}) => {
  const { t } = useTranslation();

  const customTheme: DeepPartial<FlowbiteDatepickerTheme> = {
    root: {
      input: {
        field: {
          icon: {
            base: 'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-[15px]',
          },
          input: {
            base: `pr-[15px] cursor-pointer rounded-[5px] block w-full border disabled:cursor-not-allowed disabled:opacity-50 bg-[#E8EEEE] bg-opacity-60 noto-13-normal ${inputBase}`,
            sizes: {
              sm: '',
              md: '',
              lg: '',
            },
            colors: {
              gray: `bg-[#E8EEEE] bg-opacity-60 focus:outline-none text-dark-3 ${colors}`,
              info: `bg-[#E8EEEE] bg-opacity-60 focus:outline-none  text-dark-3 ${colors}`,
              failure: `bg-[#E8EEEE] bg-opacity-60 focus:outline-none text-dark-3 ${colors}`,
              warning: `bg-[#E8EEEE] bg-opacity-60 focus:outline-none text-dark-3 ${colors}`,
              success: `bg-[#E8EEEE] bg-opacity-60 focus:outline-none text-dark-3 ${colors}`,
            },
            withAddon: {
              on: '',
              off: '',
            },
          },
        },
      },
    },
    popup: {
      root: {
        base: `${testId} absolute top-10 z-50 block pt-2`,
      },
      footer: {
        base: 'mt-2 flex justify-end',
        button: {
          base: 'rounded-[57px] min-w-[90px] w-[90px] h-[38px] text-center noto-semibold-3',
          clear:
            'flex justify-center items-center dark:bg-teal-main bg-teal-main text-white hover:bg-teal-main dark:text-white dark:hover:bg-teal-main',
          today:
            'bg-teal-main text-white hover:bg-cyan-800 dark:bg-cyan-600 dark:hover:bg-cyan-700',
        },
      },
    },
    views: {
      days: {
        items: {
          item: {
            base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center leading-9 noto-13-bold text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600',
          },
        },
      },
    },
  };

  return (
    <div data-testid={testId}>
      <Datepicker
        disabled={disabled}
        labelClearButton={t('forecast.datepicker.clean')}
        labelTodayButton={t('forecast.datepicker.today')}
        onChange={onChange}
        language="ko"
        value={date}
        className={`${className} w-[155px]`}
        icon={() => <SVGImage name="calendar-icon"></SVGImage>}
        theme={customTheme}
        showTodayButton={false}
      />
    </div>
  );
};

export default DateSelector;
